// persist.js
import { PERSIST_NOW } from '../ActionTypes.js';
import { getIsOnIOS, getIsInPwa } from '../resource/getUserAgent.js';
import { drmRulesetTestStatus as DrmTestStatus } from '../resource/drmConstants.js';

const FIVE_MINUTES = 5 * 60 * 100;
const isInPWA = getIsInPwa();
const isOnIOS = getIsOnIOS();

export const filterState = ({ state }) => {
  const tempChatPendingMessageList = state
    .getIn(['lists', 'chatroom'])
    ?.reduce((acc, value, key) => {
      const pendingMessages = value.get('pendingMessages');
      if (pendingMessages) {
        const pendingMessagesItemIds = pendingMessages
          .get('itemIds')
          .filter(id => {
            const isLocalMessageId = /-/g.test(id);
            if (isLocalMessageId) return false;
            if (state.getIn(['messages', id, 'media']))
              return state.getIn(['messages', id, 'isUploadComplete']);
          });
        acc[key] = pendingMessagesItemIds.size
          ? {
              pendingMessages: { itemIds: pendingMessagesItemIds.toJS() },
            }
          : {};
      }
      return acc;
    }, {});
  const uploadJob = state.getIn(['lists', 'uploadJob'])?.toJS() || {};
  const uploadJobMessageIds = Object.values(
    state.getIn(['operations', 'uploadJob'])?.toJS() || {}
  )
    .map(({ messageId }) => messageId)
    .filter(messageId => messageId);
  const uploadJobAssetIds = Object.values(
    state.getIn(['operations', 'uploadJob'])?.toJS() || {}
  )
    ?.map(({ assetIds }) => assetIds)
    .filter(assetIds => assetIds)
    .reduce((accumulator, current) => {
      return accumulator.concat(current);
    }, []);

  const registerEmail = state.getIn(['operations', 'register', 'email']);
  let result = {
    me: {
      token: state.getIn(['me', 'token']),
      refreshToken: state.getIn(['me', 'refreshToken']),
      defaultUsername: state.getIn(['me', 'defaultUsername']),
      orderEmail: state.getIn(['me', 'orderEmail']),
      utm: state.getIn(['me', 'utm'])?.toJS(),
      clientId: state.getIn(['me', 'clientId']),
      downloadMobileApp: state.getIn(['me', 'downloadMobileApp'])?.toJS(),
      closeDownloadSchatModalTimestamp: state.getIn([
        'me',
        'closeDownloadSchatModalTimestamp',
      ]),
      closeEmailBindingAlertTimestamp: state.getIn([
        'me',
        'closeEmailBindingAlertTimestamp',
      ]),
      notificationAsker: state.getIn(['me', 'notificationAsker'])?.toJS(),
      regionGroup: state.getIn(['me', 'regionGroup']),
      country: state.getIn(['me', 'country']),
      currency: state.getIn(['me', 'currency']),
      lastNotSupportAlertTimestamp: state.getIn([
        'me',
        'lastNotSupportAlertTimestamp',
      ]),
      lastAgeVerifierConfirmTimestamp: state.getIn([
        'me',
        'lastAgeVerifierConfirmTimestamp',
      ]),
      redirect: state.getIn(['me', 'redirect'])?.toJS(),
      hasScrollHintDisplayed: state.getIn(['me', 'hasScrollHintDisplayed']),
    },
    operations: {
      login: {
        email: state.getIn(['operations', 'login', 'email']),
      },
      freeZone: state.getIn(['operations', 'freeZone'])?.toJS(),
      register: registerEmail ? { email: registerEmail } : {},
      config: {
        currentConfigKey: state.getIn([
          'operations',
          'config',
          'currentConfigKey',
        ]),
      },
      liveStream: {
        enabledLowLatency: state.getIn([
          'operations',
          'liveStream',
          'enabledLowLatency',
        ]),
        tooltipTimestamp: state.getIn([
          'operations',
          'liveStream',
          'tooltipTimestamp',
        ]),
        lowLatencyWaitingHintTimestamp: state.getIn([
          'operations',
          'liveStream',
          'lowLatencyWaitingHintTimestamp',
        ]),
        autoPayment:
          state.getIn(['operations', 'liveStream', 'autoPayment'])?.toJS() ||
          {},
      },
      profileLists: state.getIn(['operations', 'profileLists'])?.toJS(),
      search: {
        recentSearchHashtags: state
          .getIn(['operations', 'search', 'recentSearchHashtags'])
          ?.toJS(),
        recentSearches: state
          .getIn(['operations', 'search', 'recentSearches'])
          ?.toJS(),
      },
      shop: {
        addvalue: {
          blacklistPaymentSources: state
            .getIn([
              'operations',
              'shop',
              'addvalue',
              'blacklistPaymentSources',
            ])
            ?.toJS(),
        },
      },
      drm:
        // persist `testing` state in indexedDB will causing stuck on testing next time.
        DrmTestStatus.TESTING ===
        state.getIn(['operations', 'drm', 'testStatus'])
          ? {}
          : state.getIn(['operations', 'drm'])?.toJS() || {},
      uploadJob: state.getIn(['operations', 'uploadJob'])?.toJS() || {},
      webauthn: {
        registration: {
          publicKeyCredential: state
            .getIn([
              'operations',
              'webauthn',
              'registration',
              'publicKeyCredential',
            ])
            ?.toJS(),
        },
        authentication: {
          publicKeyCredential: state
            .getIn([
              'operations',
              'webauthn',
              'authentication',
              'publicKeyCredential',
            ])
            ?.toJS(),
        },
      },
      PWA: {
        shownAtTimestamp: state.getIn([
          'operations',
          'PWA',
          'shownAtTimestamp',
        ]),
      },
      debug: state.getIn(['operations', 'debug'])?.toJS() || {},
    },
    lists: {
      chatroom: tempChatPendingMessageList || {},
      uploadJob,
    },
    messages: state
      .get('messages')
      ?.filter((data, key) => {
        const isUploadJobMessage = uploadJobMessageIds.includes(key);
        if (isUploadJobMessage) {
          return true;
        }
        const isLocalMessageId = /-/g.test(key);
        return (
          data.get('isUploadComplete') &&
          data.get('isPending') === true &&
          !isLocalMessageId
        );
      })
      .toJS(),
    assets: state
      .get('assets')
      ?.filter((data, key) => {
        return uploadJobAssetIds?.includes(key);
      })
      .toJS(),
  };
  if (isOnIOS && isInPWA) {
    result = {
      ...result,
      // save to IndexedDB doesn't means will load on refresh.
      // depends on `expireTimestamp`.
      modals: state
        .get('modals')
        .filter(
          m =>
            'display' === m.get('status') && 'PriceRefreshModal' !== m.get('id')
        )
        ?.toJS(),
      operations: state
        .get('operations')
        ?.delete('renewFeedId') // Prevent using outdated renew feed id.
        ?.delete('config') // Stop storing config data to prevent override payload from SSR.
        ?.delete('pusher') // Prevent outdated pusher data.
        ?.delete('chat') // Prevent focus/blur timing issue on iOS PWA.
        ?.deleteIn(['liveStream', 'panel']) // Prevent focus/blur timing issue on iOS PWA.
        .toJS(),
      routeHistory: {
        stack: state
          .getIn(['routeHistory', 'stack'])
          .filter(r => !/\/404|error/gi.test(r))
          .slice(-1)
          ?.toJS(),
      },
      orders: state.get('orders')?.toJS(),
      expireTimestamp: Date.now() + FIVE_MINUTES,
    };
  }
  return result;
};

export const persistNowMiddleware = store => next => async action => {
  if (action.type === PERSIST_NOW) {
    const { saveState } = await import('../resource/persist.js');
    await saveState({ state: filterState({ state: store.getState() }) });
  }
  return next(action);
};

export const persistMiddleware = store => next => async action => {
  const result = next(action);
  const { throttleSaveState } = await import('../resource/persist.js');
  throttleSaveState({
    getState: () => filterState({ state: store.getState() }),
  });
  return result;
};
